// Workspace Terminology Configuration — BILINGUAL (English + Arabic)
// ============================================================================
// Each entity / action carries BOTH an English and an Arabic value.  The
// active dashboard language (from DirectionContext) selects which set to
// show.  Switching language re-labels the whole app instantly.
//
// Shape:
//   entities.case = {
//     en: { singular: 'Case',  plural: 'Cases'  },
//     ar: { singular: 'قضية',  plural: 'قضايا' },
//   }
//   actions.create_case = { en: 'Create Case', ar: 'إنشاء قضية' }
//
// BACKWARD COMPATIBILITY: production already stores some `custom_terminology`
// in the OLD flat (English-only) shape:
//   entities.case = { singular: 'Matter', plural: 'Matters' }
//   actions.create_case = 'Create Matter'
// The normalize/merge/resolve helpers below transparently upgrade that old
// shape (treating the flat values as English) so nothing breaks for existing
// accounts.
// ============================================================================

export const TERMINOLOGY_TEMPLATES = {
  legal: {
    name: 'Legal',
    nameAr: 'قانوني',
    description: 'For law firms and legal practices',
    descriptionAr: 'لمكاتب المحاماة والممارسات القانونية',
    entities: {
      case:       { en: { singular: 'Case', plural: 'Cases' },                 ar: { singular: 'قضية', plural: 'قضايا' } },
      client:     { en: { singular: 'Client', plural: 'Clients' },             ar: { singular: 'عميل', plural: 'عملاء' } },
      session:    { en: { singular: 'Session', plural: 'Sessions' },           ar: { singular: 'جلسة', plural: 'جلسات' } },
      document:   { en: { singular: 'Document', plural: 'Documents' },         ar: { singular: 'وثيقة', plural: 'وثائق' } },
      message:    { en: { singular: 'Message', plural: 'Messages' },           ar: { singular: 'رسالة', plural: 'رسائل' } },
      connection: { en: { singular: 'Connection', plural: 'Connections' },     ar: { singular: 'اتصال', plural: 'اتصالات' } },
      employee:   { en: { singular: 'Team Member', plural: 'Team Members' },   ar: { singular: 'عضو الفريق', plural: 'أعضاء الفريق' } },
      workspace:  { en: { singular: 'Workspace', plural: 'Workspaces' },       ar: { singular: 'مساحة العمل', plural: 'مساحات العمل' } },
      owner:      { en: { singular: 'Account Owner', plural: 'Account Owners' }, ar: { singular: 'مالك الحساب', plural: 'مالكو الحساب' } },
    },
    actions: {
      create_case:      { en: 'Create Case', ar: 'إنشاء قضية' },
      view_cases:       { en: 'View Cases', ar: 'عرض القضايا' },
      add_client:       { en: 'Add Client', ar: 'إضافة عميل' },
      schedule_session: { en: 'Schedule Session', ar: 'جدولة جلسة' },
      upload_document:  { en: 'Upload Document', ar: 'رفع وثيقة' },
      send_message:     { en: 'Send Message', ar: 'إرسال رسالة' },
    },
  },

  family: {
    name: 'Family',
    nameAr: 'عائلي',
    description: 'For family photo and document archiving',
    descriptionAr: 'لأرشفة صور ومستندات العائلة',
    entities: {
      case:       { en: { singular: 'Album', plural: 'Albums' },               ar: { singular: 'ألبوم', plural: 'ألبومات' } },
      client:     { en: { singular: 'Member', plural: 'Members' },             ar: { singular: 'فرد', plural: 'أفراد' } },
      session:    { en: { singular: 'Event', plural: 'Events' },               ar: { singular: 'مناسبة', plural: 'مناسبات' } },
      document:   { en: { singular: 'Photo', plural: 'Photos' },               ar: { singular: 'صورة', plural: 'صور' } },
      message:    { en: { singular: 'Message', plural: 'Messages' },           ar: { singular: 'رسالة', plural: 'رسائل' } },
      connection: { en: { singular: 'Family Link', plural: 'Family Links' },   ar: { singular: 'رابط عائلي', plural: 'روابط عائلية' } },
      employee:   { en: { singular: 'Family Member', plural: 'Family Members' }, ar: { singular: 'فرد العائلة', plural: 'أفراد العائلة' } },
      workspace:  { en: { singular: 'Family Archive', plural: 'Family Archives' }, ar: { singular: 'أرشيف العائلة', plural: 'أرشيفات العائلة' } },
      owner:      { en: { singular: 'Family Admin', plural: 'Family Admins' }, ar: { singular: 'مدير العائلة', plural: 'مديرو العائلة' } },
    },
    actions: {
      create_case:      { en: 'Create Album', ar: 'إنشاء ألبوم' },
      view_cases:       { en: 'View Albums', ar: 'عرض الألبومات' },
      add_client:       { en: 'Add Member', ar: 'إضافة فرد' },
      schedule_session: { en: 'Create Event', ar: 'إنشاء مناسبة' },
      upload_document:  { en: 'Upload Photo', ar: 'رفع صورة' },
      send_message:     { en: 'Send Message', ar: 'إرسال رسالة' },
    },
  },

  government: {
    name: 'Government',
    nameAr: 'حكومي',
    description: 'For government agencies and departments',
    descriptionAr: 'للجهات والدوائر الحكومية',
    entities: {
      case:       { en: { singular: 'Case', plural: 'Cases' },                 ar: { singular: 'معاملة', plural: 'معاملات' } },
      client:     { en: { singular: 'Citizen', plural: 'Citizens' },           ar: { singular: 'مواطن', plural: 'مواطنون' } },
      session:    { en: { singular: 'Record', plural: 'Records' },             ar: { singular: 'سجل', plural: 'سجلات' } },
      document:   { en: { singular: 'Document', plural: 'Documents' },         ar: { singular: 'وثيقة', plural: 'وثائق' } },
      message:    { en: { singular: 'Communication', plural: 'Communications' }, ar: { singular: 'مراسلة', plural: 'مراسلات' } },
      connection: { en: { singular: 'Department Link', plural: 'Department Links' }, ar: { singular: 'رابط إداري', plural: 'روابط إدارية' } },
      employee:   { en: { singular: 'Staff Member', plural: 'Staff Members' }, ar: { singular: 'موظف', plural: 'موظفون' } },
      workspace:  { en: { singular: 'Department', plural: 'Departments' },     ar: { singular: 'قسم', plural: 'أقسام' } },
      owner:      { en: { singular: 'Department Head', plural: 'Department Heads' }, ar: { singular: 'رئيس القسم', plural: 'رؤساء الأقسام' } },
    },
    actions: {
      create_case:      { en: 'Create Case', ar: 'إنشاء معاملة' },
      view_cases:       { en: 'View Cases', ar: 'عرض المعاملات' },
      add_client:       { en: 'Add Citizen', ar: 'إضافة مواطن' },
      schedule_session: { en: 'Create Record', ar: 'إنشاء سجل' },
      upload_document:  { en: 'Upload Document', ar: 'رفع وثيقة' },
      send_message:     { en: 'Send Communication', ar: 'إرسال مراسلة' },
    },
  },

  business: {
    name: 'Business',
    nameAr: 'أعمال',
    description: 'For general business and companies',
    descriptionAr: 'للشركات والأعمال التجارية',
    entities: {
      case:       { en: { singular: 'Project', plural: 'Projects' },           ar: { singular: 'مشروع', plural: 'مشاريع' } },
      client:     { en: { singular: 'Customer', plural: 'Customers' },         ar: { singular: 'عميل', plural: 'عملاء' } },
      session:    { en: { singular: 'Meeting', plural: 'Meetings' },           ar: { singular: 'اجتماع', plural: 'اجتماعات' } },
      document:   { en: { singular: 'File', plural: 'Files' },                 ar: { singular: 'ملف', plural: 'ملفات' } },
      message:    { en: { singular: 'Message', plural: 'Messages' },           ar: { singular: 'رسالة', plural: 'رسائل' } },
      connection: { en: { singular: 'Partnership', plural: 'Partnerships' },   ar: { singular: 'شراكة', plural: 'شراكات' } },
      employee:   { en: { singular: 'Team Member', plural: 'Team Members' },   ar: { singular: 'عضو الفريق', plural: 'أعضاء الفريق' } },
      workspace:  { en: { singular: 'Organization', plural: 'Organizations' }, ar: { singular: 'مؤسسة', plural: 'مؤسسات' } },
      owner:      { en: { singular: 'Account Owner', plural: 'Account Owners' }, ar: { singular: 'مالك الحساب', plural: 'مالكو الحساب' } },
    },
    actions: {
      create_case:      { en: 'Create Project', ar: 'إنشاء مشروع' },
      view_cases:       { en: 'View Projects', ar: 'عرض المشاريع' },
      add_client:       { en: 'Add Customer', ar: 'إضافة عميل' },
      schedule_session: { en: 'Schedule Meeting', ar: 'جدولة اجتماع' },
      upload_document:  { en: 'Upload File', ar: 'رفع ملف' },
      send_message:     { en: 'Send Message', ar: 'إرسال رسالة' },
    },
  },

  medical: {
    name: 'Medical',
    nameAr: 'طبي',
    description: 'For healthcare providers and clinics',
    descriptionAr: 'لمقدّمي الرعاية الصحية والعيادات',
    entities: {
      case:       { en: { singular: 'Case', plural: 'Cases' },                 ar: { singular: 'حالة', plural: 'حالات' } },
      client:     { en: { singular: 'Patient', plural: 'Patients' },           ar: { singular: 'مريض', plural: 'مرضى' } },
      session:    { en: { singular: 'Appointment', plural: 'Appointments' },   ar: { singular: 'موعد', plural: 'مواعيد' } },
      document:   { en: { singular: 'Medical Record', plural: 'Medical Records' }, ar: { singular: 'سجل طبي', plural: 'سجلات طبية' } },
      message:    { en: { singular: 'Message', plural: 'Messages' },           ar: { singular: 'رسالة', plural: 'رسائل' } },
      connection: { en: { singular: 'Referral', plural: 'Referrals' },         ar: { singular: 'إحالة', plural: 'إحالات' } },
      employee:   { en: { singular: 'Staff Member', plural: 'Staff Members' }, ar: { singular: 'موظف', plural: 'موظفون' } },
      workspace:  { en: { singular: 'Practice', plural: 'Practices' },         ar: { singular: 'عيادة', plural: 'عيادات' } },
      owner:      { en: { singular: 'Practice Owner', plural: 'Practice Owners' }, ar: { singular: 'مالك العيادة', plural: 'مالكو العيادات' } },
    },
    actions: {
      create_case:      { en: 'Create Case', ar: 'إنشاء حالة' },
      view_cases:       { en: 'View Cases', ar: 'عرض الحالات' },
      add_client:       { en: 'Add Patient', ar: 'إضافة مريض' },
      schedule_session: { en: 'Schedule Appointment', ar: 'حجز موعد' },
      upload_document:  { en: 'Upload Medical Record', ar: 'رفع سجل طبي' },
      send_message:     { en: 'Send Message', ar: 'إرسال رسالة' },
    },
  },

  general: {
    name: 'General',
    nameAr: 'عام',
    description: 'For any other type of organization',
    descriptionAr: 'لأي نوع آخر من المؤسسات',
    entities: {
      case:       { en: { singular: 'Project', plural: 'Projects' },           ar: { singular: 'مشروع', plural: 'مشاريع' } },
      client:     { en: { singular: 'Contact', plural: 'Contacts' },           ar: { singular: 'جهة اتصال', plural: 'جهات اتصال' } },
      session:    { en: { singular: 'Event', plural: 'Events' },               ar: { singular: 'حدث', plural: 'أحداث' } },
      document:   { en: { singular: 'File', plural: 'Files' },                 ar: { singular: 'ملف', plural: 'ملفات' } },
      message:    { en: { singular: 'Message', plural: 'Messages' },           ar: { singular: 'رسالة', plural: 'رسائل' } },
      connection: { en: { singular: 'Connection', plural: 'Connections' },     ar: { singular: 'اتصال', plural: 'اتصالات' } },
      employee:   { en: { singular: 'Team Member', plural: 'Team Members' },   ar: { singular: 'عضو الفريق', plural: 'أعضاء الفريق' } },
      workspace:  { en: { singular: 'Workspace', plural: 'Workspaces' },       ar: { singular: 'مساحة العمل', plural: 'مساحات العمل' } },
      owner:      { en: { singular: 'Account Owner', plural: 'Account Owners' }, ar: { singular: 'مالك الحساب', plural: 'مالكو الحساب' } },
    },
    actions: {
      create_case:      { en: 'Create Project', ar: 'إنشاء مشروع' },
      view_cases:       { en: 'View Projects', ar: 'عرض المشاريع' },
      add_client:       { en: 'Add Contact', ar: 'إضافة جهة اتصال' },
      schedule_session: { en: 'Create Event', ar: 'إنشاء حدث' },
      upload_document:  { en: 'Upload File', ar: 'رفع ملف' },
      send_message:     { en: 'Send Message', ar: 'إرسال رسالة' },
    },
  },
}

export const LANGS = ['en', 'ar']
export const ENTITY_KEYS = ['case', 'client', 'session', 'document', 'message', 'connection', 'employee', 'workspace', 'owner']

// Default terminology (Legal)
export const DEFAULT_TERMINOLOGY = TERMINOLOGY_TEMPLATES.legal

// Helper function to get terminology for a specific type
export function getTerminologyTemplate(type) {
  return TERMINOLOGY_TEMPLATES[type] || DEFAULT_TERMINOLOGY
}

// ----------------------------------------------------------------------------
// Backward-compatibility normalizers
// ----------------------------------------------------------------------------

const ARABIC_SCRIPT = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/

/**
 * A term typed in Arabic but stored in the English slot (legacy single-language
 * data, or Arabic typed into an English field) showed Arabic words inside
 * English sentences. Move such values to the Arabic slot (unless one is already
 * set there) so English falls back to the template word.
 */
function moveArabicOutOfEnglish(out) {
  if (!out.en) return out
  for (const form of ['singular', 'plural']) {
    const v = out.en[form]
    if (v && ARABIC_SCRIPT.test(v)) {
      out.ar = out.ar || {}
      if (!out.ar[form]) out.ar[form] = v
      delete out.en[form]
    }
  }
  if (!Object.keys(out.en).length) delete out.en
  return out
}

/**
 * Normalize one entity's terms object to a partial bilingual shape
 * { en?: { singular?, plural? }, ar?: { singular?, plural? } }.
 *
 * Accepts either the new bilingual shape OR the legacy flat shape
 * ({ singular, plural }) which is treated as English.  Empty branches are
 * dropped so "has custom value" checks stay meaningful.  Arabic-script
 * values in the English slot move to Arabic (see moveArabicOutOfEnglish).
 */
function normalizeTerms(terms) {
  return moveArabicOutOfEnglish(readTerms(terms))
}

function readTerms(terms) {
  if (!terms || typeof terms !== 'object') return {}
  // New shape — already has language keys.
  if ('en' in terms || 'ar' in terms) {
    const out = {}
    if (terms.en && typeof terms.en === 'object') {
      const en = {}
      if (terms.en.singular) en.singular = terms.en.singular
      if (terms.en.plural) en.plural = terms.en.plural
      if (Object.keys(en).length) out.en = en
    }
    if (terms.ar && typeof terms.ar === 'object') {
      const ar = {}
      if (terms.ar.singular) ar.singular = terms.ar.singular
      if (terms.ar.plural) ar.plural = terms.ar.plural
      if (Object.keys(ar).length) out.ar = ar
    }
    return out
  }
  // Legacy flat shape → English.
  const en = {}
  if (terms.singular) en.singular = terms.singular
  if (terms.plural) en.plural = terms.plural
  return Object.keys(en).length ? { en } : {}
}

/**
 * Normalize one action value to { en?, ar? }.  Accepts a bilingual object
 * OR a legacy plain string (treated as English).
 */
function normalizeAction(val) {
  const out = readAction(val)
  // Arabic text in the English slot belongs to Arabic (see moveArabicOutOfEnglish).
  if (out.en && ARABIC_SCRIPT.test(out.en)) {
    if (!out.ar) out.ar = out.en
    delete out.en
  }
  return out
}

function readAction(val) {
  if (val == null) return {}
  if (typeof val === 'string') {
    return val ? { en: val } : {}
  }
  if (typeof val === 'object') {
    const out = {}
    if (val.en) out.en = val.en
    if (val.ar) out.ar = val.ar
    return out
  }
  return {}
}

/**
 * Normalize a whole custom_terminology object (as stored on the account) into
 * the partial bilingual shape used by the editor + merge.  Safe for both the
 * legacy flat shape and the new bilingual shape.
 */
export function normalizeCustomTerminology(custom = {}) {
  const out = {}
  if (custom && custom.entities && typeof custom.entities === 'object') {
    const entities = {}
    for (const [key, terms] of Object.entries(custom.entities)) {
      const n = normalizeTerms(terms)
      if (Object.keys(n).length) entities[key] = n
    }
    if (Object.keys(entities).length) out.entities = entities
  }
  if (custom && custom.actions && typeof custom.actions === 'object') {
    const actions = {}
    for (const [key, val] of Object.entries(custom.actions)) {
      const n = normalizeAction(val)
      if (Object.keys(n).length) actions[key] = n
    }
    if (Object.keys(actions).length) out.actions = actions
  }
  return out
}

// ----------------------------------------------------------------------------
// Merge (bilingual template + bilingual/legacy custom) -> bilingual merged
// ----------------------------------------------------------------------------

/**
 * Deep-merge custom terminology over a template, keeping BOTH languages.
 * Returns a fully-populated bilingual structure: every template entity has
 * en+ar singular+plural, with any custom override winning per-field-per-lang.
 */
export function mergeTerminology(templateType, customTerminology = {}) {
  const template = getTerminologyTemplate(templateType)
  const custom = normalizeCustomTerminology(customTerminology)
  const customEntities = custom.entities || {}
  const customActions = custom.actions || {}

  const mergedEntities = {}
  for (const [key, t] of Object.entries(template.entities)) {
    const c = customEntities[key] || {}
    mergedEntities[key] = {
      en: {
        singular: c.en?.singular || t.en.singular,
        plural:   c.en?.plural   || t.en.plural,
      },
      ar: {
        singular: c.ar?.singular || t.ar.singular,
        plural:   c.ar?.plural   || t.ar.plural,
      },
    }
  }

  const mergedActions = {}
  for (const [key, t] of Object.entries(template.actions)) {
    const c = customActions[key] || {}
    mergedActions[key] = {
      en: c.en || t.en,
      ar: c.ar || t.ar,
    }
  }

  return { ...template, entities: mergedEntities, actions: mergedActions }
}

// ----------------------------------------------------------------------------
// Resolve (bilingual merged) -> single-language flat terminology
// ----------------------------------------------------------------------------

/**
 * Flatten a bilingual merged terminology into the single-language flat shape
 * the rest of the app already expects:
 *   { entities: { case: { singular, plural }, ... }, actions: { create_case: 'Create Case', ... } }
 *
 * `lang` selects the language; missing values fall back to English, then to a
 * sensible default derived from the key.  This is what makes EVERY existing
 * call site (getEntity('case').singular, t('case'), getAction('create_case'))
 * automatically language-aware with no changes.
 */
export function resolveTerminology(bilingual, lang = 'en') {
  if (!bilingual) return null
  const entities = {}
  for (const [key, terms] of Object.entries(bilingual.entities || {})) {
    const l = terms[lang] || {}
    const en = terms.en || {}
    entities[key] = {
      singular: l.singular || en.singular || key,
      plural:   l.plural   || en.plural   || `${key}s`,
    }
  }
  const actions = {}
  for (const [key, val] of Object.entries(bilingual.actions || {})) {
    actions[key] = (val && (val[lang] || val.en)) || key
  }
  return { ...bilingual, entities, actions }
}

// Helper to get a specific term (kept for compatibility; operates on the
// FLAT resolved terminology, not the bilingual one).
export function getTerm(terminology, path) {
  const parts = path.split('.')
  let result = terminology
  for (const part of parts) {
    if (result && typeof result === 'object') {
      result = result[part]
    } else {
      return null
    }
  }
  return result || path
}
